import React, { createContext, useContext, useEffect, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { draftsApi } from "../../apis/drafts";
import { usePermissions } from "../../hooks/usePermissions";
import { SalesFormStoreProvider, useSalesFormStoreApi } from "../../services/useSalesFormStore";
import { createDraftController, saveAndMinimize } from "./controller";
import { eligibleEntry, entrySnapshot, snapshotStore, validateDraft } from "./snapshot";
import { useDrafts } from "./DraftsContext";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { getSessionId } from "../../utils/SessionHandler";
import { AlertCircle } from "lucide-react";

const DraftFormContext = createContext(null);
export const useDraftForm = () => useContext(DraftFormContext);

function DraftAdapter({ draft, entry, navigationState, children }) {
  const store = useSalesFormStoreApi();
  const dock = useDrafts();
  const workspace = useWorkspace();
  const activeRegistration = dock?.active;
  const rights = usePermissions(entry.transactionCode);
  // No visible draft header: drafts are titled automatically and listed in the dock/taskbar by code.
  const title = draft?.title || `${entry.transactionCode} draft`;
  const [, render] = useReducer((n) => n + 1, 0);
  const [localError, setLocalError] = useState("");
  const latest = useRef({ title, dock, canAdd: rights.can_add });
  latest.current = { title, dock, canAdd: rights.can_add };
  const alive = useRef(true);
  const session = useRef(getSessionId());
  const [controller] = useState(() => createDraftController({
    draft, code: entry.transactionCode,
    read: () => ({ title: latest.current.title, payload: { entry, snapshot: snapshotStore(store.getState()) } }),
    notify: () => { if (alive.current) render(); },
    isActive: () => alive.current && session.current === getSessionId(),
    onSaved: () => { if (alive.current && !controller.status().minimizing) latest.current.dock?.refresh(); },
    onCommitted: (id) => { if (alive.current) latest.current.dock?.removeCommitted(id); },
  }));
  useEffect(() => {
    alive.current = true;
    const unsubscribe = store.subscribe(() => { if (alive.current) render(); });
    const registration = {
      dirty: () => !controller.status().committed && controller.status().dirty,
      blocked: () => controller.status().saving || controller.status().submitting || controller.status().minimizing,
      id: () => controller.status().draft?.id,
    };
    if (activeRegistration) activeRegistration.current = registration;
    return () => {
      alive.current = false;
      unsubscribe();
      if (activeRegistration?.current === registration) activeRegistration.current = null;
    };
  }, [controller, store, activeRegistration]);
  // The global Minimize (header) saves this entry first and reopens it from the
  // taskbar via its draft ID. A failed save keeps the form open with its error.
  useEffect(() => workspace?.registerMinimizeHandler(async () => {
    if (!latest.current.canAdd) {
      const message = "Add permission is required to save or minimize this entry.";
      setLocalError(message);
      throw new Error(message);
    }
    setLocalError("");
    let saved = null;
    try {
      await saveAndMinimize(controller, () => {}, entry.path, (result) => { saved = result; latest.current.dock?.showMinimized(result); });
    } catch (e) { if (alive.current) setLocalError(e.message); throw e; }
    return {
      title: latest.current.title?.trim() || `${entry.transactionCode} draft`,
      subtitle: entry.transactionCode,
      pathname: entry.path,
      search: `?draft=${encodeURIComponent(saved.id)}`,
      state: { transactionCode: entry.transactionCode },
    };
  }), [workspace, controller, entry.path, entry.transactionCode]);
  const status = controller.status();
  const saveDraft = async () => {
    if (!rights.can_add) return;
    setLocalError("");
    try { await controller.save(); } catch (e) { if (alive.current) setLocalError(e.message); }
  };
  const value = { controller, entry, navigationState, restored: Boolean(draft), canAdd: rights.can_add, saveDraft, ...status };
  const error = localError || status.error;
  return <DraftFormContext.Provider value={value}>
    {(error || !rights.can_add) && <div className="draft-alerts">
      {error && <p className="draft-alert" role="alert"><AlertCircle size={16} aria-hidden="true" />{error}</p>}
      {!rights.can_add && <p className="draft-alert" role="alert"><AlertCircle size={16} aria-hidden="true" />Add permission is required to save or submit this entry.</p>}
    </div>}
    <fieldset className="draft-entry-fields" disabled={!rights.can_add || status.submitting || status.minimizing || status.committed}>{children}</fieldset>
  </DraftFormContext.Provider>;
}

export function DraftEntryBoundary({ children }) {
  const location = useLocation();
  const id = new URLSearchParams(location.search).get("draft");
  // Route state is only a candidate hint; current server settings decide the type.
  const candidate = eligibleEntry({ ...location.state, transactionSettings: undefined }, location.pathname);
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState("");
  const [attempt, retry] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    if (!id && !candidate) return;
    let active = true;
    setError("");
    setLoaded(null);
    const load = async () => {
      const draft = id ? await draftsApi.get(id) : null;
      const validated = id
        ? validateDraft(draft, location.state?.transactionCode, location.pathname)
        : { entry: entrySnapshot(location.state, location.pathname) };
      const settings = await draftsApi.getTransactionSettings(validated.entry.transactionCode);
      if (settings.tran_code !== undefined && settings.tran_code !== validated.entry.transactionCode) {
        throw new Error("Transaction settings do not match this entry.");
      }
      if (settings.type !== "transaction") {
        if (id) throw new Error("This draft's transaction type no longer supports generic entry.");
        if (active) setLoaded({ legacy: true });
        return;
      }
      const entry = {
        ...validated.entry,
        isAutoTransactionNo: settings.is_auto_transaction_no,
        pickupMandatory: settings.is_pick_up_mandatory,
        backDatedEntry: settings.is_back_dated_entry_allowed,
      };
      // New entries still need company/rowData and other initial mapping inputs.
      // Keep these out of the whitelisted entry persisted by the controller.
      const navigationState = { ...(!id ? location.state : {}), ...entry, transactionSettings: settings };
      if (active) setLoaded({ ...validated, draft, entry, navigationState });
    };
    load().catch((e) => { if (active) setError(e.message || "Unable to load transaction settings."); });
    return () => { active = false; };
  }, [id, candidate, location.pathname, location.state, attempt]);
  if (!id && !candidate) return children;
  if (error) return <div role="alert">{error} {id && "Your draft has not been changed."} <button onClick={() => { setError(""); retry(); }}>Retry</button></div>;
  if (!loaded) return <p role="status">{id ? "Loading private draft…" : "Loading transaction settings…"}</p>;
  if (loaded.legacy) return children;
  return <SalesFormStoreProvider snapshot={loaded.snapshot}>
    <DraftAdapter draft={loaded.draft} entry={loaded.entry} navigationState={loaded.navigationState}>{children}</DraftAdapter>
  </SalesFormStoreProvider>;
}